/**
 * AI referral detection: classifies a visit as coming from an AI assistant
 * (ChatGPT, Perplexity, Claude, Gemini, Grok, Copilot, ...) using two signals:
 *
 *   1. Referrer hostname, matched as exact host or dot-suffix. Never substring
 *      of the full URL, so "notopenai.com" can't match "openai.com".
 *   2. utm_source query param. ChatGPT appends utm_source=chatgpt.com to cited
 *      links and strips the referrer on mobile/app traffic, so this catches
 *      visits the referrer never could.
 *
 * Known blind spots (industry-wide, not fixable client-side):
 *   - Google AI Overviews / AI Mode traffic arrives as plain google.com and is
 *     indistinguishable from organic search. Never classify google.com as AI.
 *   - Grok answers inside X arrive as x.com (looks like social). Only grok.com
 *     referrers are reliably Grok.
 *   - Bare bing.com is ambiguous with Bing organic; only Copilot-specific
 *     hosts are matched.
 */

export interface AiSource {
  /** Stable key stored in ai_referrals.source */
  key: string;
  /** Display name for the admin dashboard */
  label: string;
  /** Referrer hostnames (matched exact or as ".host" suffix) */
  hosts: string[];
  /** utm_source values (matched case-insensitively, exact) */
  utmSources: string[];
}

export const AI_SOURCES: AiSource[] = [
  {
    key: 'chatgpt',
    label: 'ChatGPT',
    hosts: ['chatgpt.com', 'chat.openai.com', 'openai.com'],
    utmSources: ['chatgpt.com', 'chatgpt', 'openai'],
  },
  {
    key: 'perplexity',
    label: 'Perplexity',
    hosts: ['perplexity.ai'],
    utmSources: ['perplexity', 'perplexity.ai'],
  },
  {
    key: 'claude',
    label: 'Claude',
    hosts: ['claude.ai'],
    utmSources: ['claude', 'claude.ai', 'anthropic'],
  },
  {
    key: 'gemini',
    label: 'Google Gemini',
    hosts: ['gemini.google.com', 'bard.google.com'],
    utmSources: ['gemini'],
  },
  {
    key: 'copilot',
    label: 'Microsoft Copilot',
    hosts: [
      'copilot.microsoft.com',
      'copilot.com',
      'edgeservices.bing.com',
      'm365.cloud.microsoft',
      'copilot.cloud.microsoft',
    ],
    utmSources: ['copilot'],
  },
  {
    key: 'grok',
    label: 'Grok',
    hosts: ['grok.com'],
    utmSources: ['grok'],
  },
  {
    key: 'meta_ai',
    label: 'Meta AI',
    hosts: ['meta.ai'],
    utmSources: ['meta.ai'],
  },
  {
    key: 'deepseek',
    label: 'DeepSeek',
    hosts: ['chat.deepseek.com', 'deepseek.com'],
    utmSources: ['deepseek'],
  },
  {
    key: 'mistral',
    label: 'Mistral Le Chat',
    hosts: ['chat.mistral.ai', 'mistral.ai'],
    utmSources: ['mistral'],
  },
  {
    key: 'you',
    label: 'You.com',
    hosts: ['you.com'],
    utmSources: ['you.com'],
  },
  {
    key: 'poe',
    label: 'Poe',
    hosts: ['poe.com'],
    utmSources: ['poe'],
  },
  {
    key: 'phind',
    label: 'Phind',
    hosts: ['phind.com'],
    utmSources: ['phind'],
  },
  {
    key: 'other_ai',
    label: 'Other AI',
    hosts: ['pi.ai', 'character.ai', 'blackbox.ai', 'komo.ai', 'andisearch.com', 'huggingface.co'],
    utmSources: [],
  },
];

const SOURCE_BY_KEY = new Map(AI_SOURCES.map((s) => [s.key, s]));

export function getAiSourceLabel(key: string): string {
  return SOURCE_BY_KEY.get(key)?.label || key;
}

function hostMatches(host: string, candidate: string): boolean {
  return host === candidate || host.endsWith(`.${candidate}`);
}

/**
 * Classify a bare source string, e.g. a GA4 sessionSource value like
 * "chatgpt.com" or "perplexity". These are hostnames or utm_source values,
 * not full URLs, so they're matched against both lists directly.
 */
export function classifySourceString(value: string | null | undefined): AiSource | null {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return null;
  for (const source of AI_SOURCES) {
    if (source.hosts.some((h) => hostMatches(normalized, h))) return source;
    if (source.utmSources.includes(normalized)) return source;
  }
  return null;
}

/**
 * Classify a visit. Pass the raw referrer URL (document.referrer or the
 * Referer header) and the raw utm_source param. Returns the matching source,
 * or null when the visit is not from a known AI platform.
 */
export function classifyAiReferral(
  referrer: string | null | undefined,
  utmSource: string | null | undefined
): AiSource | null {
  let referrerHost: string | null = null;
  if (referrer) {
    try {
      referrerHost = new URL(referrer).hostname.toLowerCase();
    } catch {
      referrerHost = null;
    }
  }

  if (referrerHost) {
    for (const source of AI_SOURCES) {
      if (source.hosts.some((h) => hostMatches(referrerHost, h))) {
        return source;
      }
    }
  }

  if (utmSource) {
    const normalized = utmSource.trim().toLowerCase();
    for (const source of AI_SOURCES) {
      if (source.utmSources.includes(normalized)) {
        return source;
      }
    }
  }

  return null;
}
